import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { HistoryAdvancedSearchProps } from "./types";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as SearchIcon } from "assets/images/icons/search.svg";

import { HistoryFilter } from "../types";
import { messages, movementTypeOptions } from "./messages";
import { Button, Checkbox } from "@letsbit/milkylb";
import { ReactComponent as SimpleArrowIcon } from "assets/images/icons/simple-arrow.svg";
import { HISTORY_OPERATION_TYPE, RECORD_STATE } from "@letsbit/malcolmlb";
import { BackButton } from "components/BackButton";
import { MultiCurrencyPicker } from "components/MultiCurrencyPicker";
import { useCurrencies } from "hooks/user/useCurrencies";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as ResumeIcon } from "assets/images/icons/resume.svg";
import DatePicker from "../../DatePicker/DatePicker";
import dayjs from "dayjs";
import { ModalSelector } from "components/ModalSelector";
import { CardsToIncludeSelector } from "../CardsToIncludeSelector";
import { useGetCards } from "hooks/kaibacorp/useGetCards";

export const HistoryAdvancedSearch: React.FC<HistoryAdvancedSearchProps> = ({
  filter,
  setFilter,
  onClose,
  memberUid,
}) => {
  const intl = useIntl();
  const { handleSubmit, control, watch, setValue, getValues } =
    useForm<HistoryFilter>({
      defaultValues: {
        ...filter,
      },
    });

  const { data: currencies } = useCurrencies();
  const { data: cards } = useGetCards();
  const navigate = useNavigate();

  const isCardTransaction =
    watch("operationType")?.[0] === HISTORY_OPERATION_TYPE.CARD_TRANSACTIONS;

  useEffect(() => {
    if (isCardTransaction) {
      setValue("currencies", new Set());
    } else {
      setValue("cards", new Set());
    }
  }, [isCardTransaction, setValue]);

  const formatOptionMessage = (option: HISTORY_OPERATION_TYPE) => {
    const message = movementTypeOptions.find((o) => o.id === option);

    if (message) {
      return intl.formatMessage(message.description);
    }
  };
  return (
    <form
      className="flex h-full flex-col justify-between"
      onSubmit={handleSubmit((data) => {
        setFilter(data);
        onClose();
      })}
    >
      <div>
        <div className="mb-8 flex items-center justify-between border-b border-[--milkylb-color-secondary-100] pb-8">
          <div>
            <BackButton className="mb-4" onClick={onClose} />
            <h1 className="font-['Tomato_Grotesk'] text-4xl font-bold">
              <FormattedMessage {...messages.title} />
            </h1>
          </div>
          <div className="flex gap-4">
            <Link to="/logged/history/montlhy-resume">
              <Button
                fill="clear"
                className="letsbit-ui-button-outline-svg-filled"
              >
                <ResumeIcon className="mr-2" />
                <FormattedMessage {...messages.resumeButton} />
              </Button>
            </Link>
            <Button
              onClick={() =>
                navigate(
                  memberUid
                    ? `/logged/members/${memberUid}/platform/history`
                    : "/logged/history",
                  { state: getValues() },
                )
              }
            >
              <SearchIcon className="mr-2 fill-white" />
              <FormattedMessage {...messages.search} />
            </Button>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="flex gap-8">
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.movementType} />
              </h3>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="mb-8 w-[420px] gap-2">
                    <ModalSelector
                      classNameModalWrapper="letsbit-tight-modal"
                      modalProps={{ className: "" }}
                      placeholder={intl.formatMessage(
                        messages.selectMovementType,
                      )}
                      title={
                        <h3 className="mb-4 text-center text-xl font-bold text-[--color-navbar-logo]">
                          <FormattedMessage {...messages.selectCurrency} />
                        </h3>
                      }
                      optionComponent={(oc) => (
                        <div className="my-4 flex flex-row justify-between text-[#666666] hover:text-black">
                          {intl.formatMessage(oc.element.description)}
                          <SimpleArrowIcon className="rotate-180" />
                        </div>
                      )}
                      value={value ? formatOptionMessage(value[0]) : undefined}
                      options={movementTypeOptions}
                      onSelectOption={(c) => {
                        onChange([c.id]);
                      }}
                      dataCypressId="concept-selector"
                    />
                  </div>
                )}
                name="operationType"
              />
            </div>
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.state} />
              </h3>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="mb-8 grid w-[420px] grid-cols-2 gap-2">
                    <Checkbox
                      checked={value.has(RECORD_STATE.SUCCEED)}
                      onIonChange={(e: { target: { checked: boolean } }) => {
                        if (e.target.checked) {
                          value.add(RECORD_STATE.SUCCEED);
                        } else {
                          value.delete(RECORD_STATE.SUCCEED);
                        }
                        onChange(value);
                      }}
                      justify="start"
                      labelPlacement="end"
                    >
                      <FormattedMessage {...messages.approved} />
                    </Checkbox>
                    <Checkbox
                      checked={value.has(RECORD_STATE.FAILED)}
                      onIonChange={(e: { target: { checked: boolean } }) => {
                        if (e.target.checked) {
                          value.add(RECORD_STATE.FAILED);
                        } else {
                          value.delete(RECORD_STATE.FAILED);
                        }
                        onChange(value);
                      }}
                      justify="start"
                      labelPlacement="end"
                    >
                      <FormattedMessage {...messages.rejected} />
                    </Checkbox>
                    <Checkbox
                      checked={value.has(RECORD_STATE.PENDING)}
                      onIonChange={(e: { target: { checked: boolean } }) => {
                        if (e.target.checked) {
                          value.add(RECORD_STATE.PENDING);
                        } else {
                          value.delete(RECORD_STATE.PENDING);
                        }
                        onChange(value);
                      }}
                      justify="start"
                      labelPlacement="end"
                    >
                      <FormattedMessage {...messages.pending} />
                    </Checkbox>
                    <Checkbox
                      checked={value.has(RECORD_STATE.FAILED)}
                      onIonChange={(e: { target: { checked: boolean } }) => {
                        if (e.target.checked) {
                          value.add(RECORD_STATE.FAILED);
                        } else {
                          value.delete(RECORD_STATE.FAILED);
                        }
                        onChange(value);
                      }}
                      justify="start"
                      labelPlacement="end"
                    >
                      <FormattedMessage {...messages.withErrors} />
                    </Checkbox>
                  </div>
                )}
                name="states"
              />
            </div>
          </div>
          {isCardTransaction && cards ? (
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.cardsToInclude} />
              </h3>
              <Controller
                key="cards-selector"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="mb-8 w-[420px] gap-2">
                    <CardsToIncludeSelector
                      title={
                        <h3 className="mb-8 text-center text-xl font-bold text-[--card-color-classic]">
                          <FormattedMessage {...messages.cardsSelectorTitle} />
                        </h3>
                      }
                      onConfirmSelection={onChange}
                      selectedIdCards={value}
                      cards={cards}
                    />
                  </div>
                )}
                name="cards"
              />
            </div>
          ) : (
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.selectCurrency} />
              </h3>
              <Controller
                key="currencies-selector"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="mb-8 w-[420px] gap-2">
                    <MultiCurrencyPicker
                      title={
                        <h3 className="mb-8 text-center text-xl font-bold">
                          <FormattedMessage {...messages.sendTitle} />
                        </h3>
                      }
                      onSelectCurrencies={(selectedCurrencies) => {
                        onChange({
                          target: {
                            value: new Set(selectedCurrencies),
                          },
                        });
                        currencies;
                      }}
                      selectedCurrencies={value}
                      currencies={currencies}
                    />
                  </div>
                )}
                name="currencies"
              />
            </div>
          )}
          <div className="mb-8 grid w-[420px] grid-cols-2 gap-2">
            <div className="flex flex-col">
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.since} />
              </h3>
              <Controller
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="mb-8 grid-cols-2 gap-2">
                    <DatePicker
                      floatingLabel={intl.formatMessage(messages.chooseDate)}
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        if (event === null) {
                          return;
                        }
                        onChange(event?.toString());
                      }}
                      error={error}
                    />
                  </div>
                )}
                name="from"
              />
            </div>
            <div className="flex flex-col">
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.until} />
              </h3>
              <Controller
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="mb-8 grid-cols-2 gap-2">
                    <DatePicker
                      floatingLabel={intl.formatMessage(messages.chooseDate)}
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        if (event === null) {
                          return;
                        }
                        onChange(event?.toString());
                      }}
                      error={error}
                    />
                  </div>
                )}
                name="to"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
